import React, { useState, useEffect, useRef } from 'react';

import './Chat.css';

import { Conversation, Receivable } from '@dndModels';

// import DandDCharacterUtilities from "@dndUtilities/char_utilities";
import CommonUtilities from "@utilities/common_utilities";
// import { makeStyles } from "@utilities/theme_utilities";

import ModalOrDrawer from "@components/display/ModalOrDrawer";
import DandDAPI from '@utilities/smart_api';
import FlexBox from '@components/layout/FlexBox';
import DandDButtonBox from '@dndComponents/DandDButtonBoxSimple';
import StringBox from '@components/input/strings/StringBox';
import SendButton from '@components/input/buttons/icon_buttons/SendButton';
import DeleteButton from '@components/input/buttons/icon_buttons/DeleteButton';

interface Props {
  convo: Conversation;
  ping_time: number;
  onDelete: () => void;
}

const api = DandDAPI.getInstance();
// const char_util = DandDCharacterUtilities.getInstance();
const common_util = CommonUtilities.getInstance();

export default function ChatBox({ 
  convo,
  ping_time,
  onDelete
}: Props) {
  const [reloading, setReloading] = useState(false);
  // const [processing, setProcessing] = useState(false);
  const [new_message, setNewMessage] = useState("");
  const [ping, setPing] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [prev_ping_time, setPrevPingTime] = useState(ping_time);
  const [change_time, setChangeTime] = useState(0);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  useEffect(() => {
    if (ping_time !== prev_ping_time) {
      setPrevPingTime(ping_time);
    }
  }, [ping_time]);

  useEffect(() => {
    if (convo.open && !wasOpenRef.current) {
      // Modal just opened — wait a tick so the message list has rendered
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
      }, 100);
    }
    wasOpenRef.current = convo.open;
  }, [ping]);

  // const useStyles = makeStyles()((theme) => {
  //   return ({
  //     root: {
  //       border: `1px solid ${theme.palette.divider}`,
  //       borderRadius: '4px',
  //       '&:not(.disabled)': {
  //         backgroundColor: theme.palette.secondary.main,
  //       },
  //       '&.disabled': {
  //         backgroundColor: "gray",
  //         color: "black"
  //       }
  //     },
  //   });
  // });

  // const { classes, cx } = useStyles();

  const renderExtras = () => {
    return (
      <>
        <ModalOrDrawer
          open={ convo.open } 
          onClose={ async () => {
            convo.open = false;
            if (api.user) {
              api.user.convo_seen_times.setSeenTime(convo._id, common_util.get_time_number());
              await api.upsertObject("convo_seen_times", api.user.convo_seen_times);
            }
            setPing(!ping);
          }}>
          { renderOpenConversation() }
        </ModalOrDrawer>
      </>
    );
  }

  const renderConversation = () => {
    return (
      <FlexBox key={convo._id}>
        <DandDButtonBox
          name={`${convo.name}${convo.type === "Admin" ? " (Admin Conversation)" : ""}`}
          className={ convo.flashing ? "Flashing" : "" }
          style={{ backgroundColor: "white", color: "black", minWidth: "200px" }}
          onClick={ async () => {
            convo.open = true;
            convo.flashing = false;
            if (api.user) {
              api.user.convo_seen_times.setSeenTime(convo._id, common_util.get_time_number());
              await api.upsertObject("convo_seen_times", api.user.convo_seen_times);
            }
            setPing(!ping);
          }}
        />
      </FlexBox>
    );
  }

  const sendMessage = async (msg: string) => {
    setProcessing(true);
    setNewMessage("");
    setTimeout(() => {
      setChangeTime(common_util.get_time_number());
    }, 100);
    const receivable = new Receivable();
    receivable.from_id = api.user_id;
    receivable.conversation_id = convo._id;
    receivable.admin = convo.type === "Admin";
    if (convo.type === "User" || convo.type === "Admin") {
      receivable.user_ids = convo.ids;
    } else {
      receivable.campaign_id = convo.ids[0];
    }
    receivable.sent_dt = common_util.get_time_number();
    receivable.message = msg;
    receivable.origin_id = api.temp_id;
    receivable.type = "Chat";
    await api.upsertObject("receivable", receivable);
    convo.messages.push(receivable);
    convo.last_message_time = common_util.get_time_number();
    await api.upsertObject("conversation", convo);
    if (api.user) {
      api.user.convo_seen_times.setSeenTime(convo._id, common_util.get_time_number());
      await api.upsertObject("convo_seen_times", api.user.convo_seen_times);
    }
    setReloading(true);
    setProcessing(false);
  }

  const renderOpenConversation = () => {
    const messages = convo.messages;
    // console.log(messages);
    return (
      <FlexBox direction="column" style={{ height: "100%" }}>
        <FlexBox xs_expand={0} direction="row">
          <FlexBox className="header centered">
            Conversation with { convo.name } {convo.type === "Admin" ? "(Admin Conversation)" : ""}
          </FlexBox>
          <FlexBox xs_expand={0} className="float-right">
            <DeleteButton 
              obj_name={`Conversation with ${ convo.name }${convo.type === "Admin" ? " (Admin Conversation)" : ""}`}
              onClick={() => {
                convo.open = false;
                convo.hidden = false;
                setPing(!ping);
                onDelete();
              }}
            />
          </FlexBox>
        </FlexBox>
        <FlexBox xs_expand={1} direction="column" style={{ overflowY: "auto", height: "calc(100vh - 140px)" }}>
          { !reloading && messages.sort((a, b) => {
            return a.sent_dt - b.sent_dt;
          }).map(msg => renderMessage(msg))}
          <div ref={messagesEndRef} />
        </FlexBox>
        <FlexBox xs_expand={0} direction="row" className="centered">
          <FlexBox>
            <StringBox
              name=""
              value={new_message}
              change_time={change_time}
              onKeyDown={ (e, current_value) => {
                if (e.key === "Enter") {
                  setTimeout(() => {
                    sendMessage(current_value);
                  }, 100);
                }
              }}
              onBlur={(changed: string) => {
                if (!processing) {
                  setNewMessage(changed);
                }
              }}
            />
          </FlexBox>
          <FlexBox xs_expand={0}>
            <SendButton
              disabled={processing || messages.length === 0}
              onClick={() => sendMessage(new_message) }
            />
          </FlexBox>
        </FlexBox>
      </FlexBox>
    );
  }

  const renderMessage = (msg: Receivable) => {
    if (msg.from_id === api.user_id) {
      return (
        <FlexBox key={msg._id} xs_expand={0} xs_shrink={0} className="padded">
          <FlexBox direction="column" className="bordered float-right" style={{ backgroundColor: "white", color: "black" }}>
            <FlexBox className="small_label gray padded">Me - { common_util.time_string(msg.sent_dt) }</FlexBox>
            <FlexBox className="padded">
              { msg.message }
            </FlexBox>
          </FlexBox>
        </FlexBox>
      );
    } else {
      const from = api.my_users.find(o => o._id === msg.from_id);
      if (from) {
        return (
          <FlexBox key={msg._id} xs_expand={0} xs_shrink={0} className="padded">
            <FlexBox direction="column" className="bordered float-left" style={{ backgroundColor: "white", color: "black" }}>
              <FlexBox className="small_label gray padded">{ from.name } - { common_util.time_string(msg.sent_dt) }</FlexBox>
              <FlexBox className="padded">
                { msg.message }
              </FlexBox>
            </FlexBox>
          </FlexBox>
        );
      }
    }
    return null;
  }

  return (
    <>
      { renderConversation() }
      { renderExtras() }
    </>
  );
}
